import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  Alert,
  MenuItem,
  IconButton,
  Typography,
  Avatar,
  InputAdornment,
  Divider,
  Chip,
} from "@mui/material";
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  ToggleOn as ToggleOnIcon,
} from "@mui/icons-material";
import { editBrokerAccount } from "../../../Services/BrokersApi";
import { themeColors } from "../../../theme/colors";

const EditBrokerAccountDialog = ({ open, account = null, onClose, onUpdated }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState(""); // تركها فارغة يعني لا تغيّر كلمة المرور
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [active, setActive] = useState(""); // use "1" or "0"
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // عند فتح الحوار أو تغيّر account, عبّئ الحقول الحالية
  useEffect(() => {
    if (!open) return;
    if (account) {
      setEmail(account.email ?? "");
      setName(account.name ?? "");
      setPhone(account.phone ?? "");
      // account.active قد يكون "1"/"0" أو 1/0
      setActive(account.active != null ? String(account.active) : "");
      setPassword(""); // لا نعرض كلمة المرور الحالية
    } else {
      setEmail("");
      setName("");
      setPhone("");
      setActive("");
      setPassword("");
    }
    setError(null);
  }, [open, account]);

  const handleSave = async () => {
    if (!account || !account.id) {
      setError("حساب غير محدد");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("authToken");
      const payload = {};
      if (email !== "") payload.email = email;
      if (password !== "") payload.password = password; // إذا ترك فارغ فلن يغير
      if (name !== "") payload.name = name;
      if (phone !== "") payload.phone = phone;
      if (active !== "") payload.active = active; // إرسال كـ "1" أو "0"

      await editBrokerAccount(account.id, payload, token);
      if (onUpdated) onUpdated();
    } catch (err) {
      setError((err && (err.message || err.msg || JSON.stringify(err))) || "فشل التحديث");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          pb: 2,
          background: `linear-gradient(135deg, ${themeColors.secondary.main} 0%, ${themeColors.secondary.dark} 100%)`,
          color: "white",
          position: "relative",
        }}
      >
        <Avatar
          sx={{
            width: 48,
            height: 48,
            bgcolor: "rgba(255,255,255,0.25)",
            backdropFilter: "blur(10px)",
          }}
        >
          <EditIcon />
        </Avatar>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            تعديل بيانات الحساب
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.9 }}>
            ID: {account?.id ?? "-"} • {account?.tenant_id ? `النسخة: ${account.tenant_id}` : "لا توجد نسخة"}
          </Typography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            left: 8,
            top: 8,
            color: "white",
            "&:hover": {
              bgcolor: "rgba(255,255,255,0.15)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 3,
              borderRadius: 2,
              boxShadow: "0 2px 8px rgba(220, 53, 69, 0.15)",
            }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {/* Account Status Chips */}
        <Box display="flex" gap={1} mb={3} flexWrap="wrap">
          <Chip
            label={account?.active === 1 ? "الحساب يعمل" : "الحساب معطل"}
            size="small"
            sx={{
              bgcolor: account?.active === 1 ? `${themeColors.success}15` : `${themeColors.error}15`,
              color: account?.active === 1 ? themeColors.success : themeColors.error,
              fontWeight: 600,
            }}
          />
          <Chip
            label={account?.status === 1 ? "مفعل" : "غير مفعل"}
            size="small"
            variant="outlined"
            sx={{
              borderColor: account?.status === 1 ? themeColors.success : themeColors.error,
              color: account?.status === 1 ? themeColors.success : themeColors.error,
            }}
          />
          <Chip
            label={account?.system_status === 1 ? "النظام مفعل" : "النظام معطل"}
            size="small"
            variant="outlined"
            sx={{
              borderColor: account?.system_status === 1 ? themeColors.info : themeColors.warning,
              color: account?.system_status === 1 ? themeColors.info : themeColors.warning,
            }}
          />
        </Box>

        <Box display="flex" flexDirection="column" gap={2.5}>
          <TextField
            label="البريد الإلكتروني"
            placeholder="example@domain.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon sx={{ color: themeColors.text.secondary }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                "&:hover fieldset": {
                  borderColor: themeColors.secondary.main,
                },
                "&.Mui-focused fieldset": {
                  borderColor: themeColors.secondary.main,
                },
              },
            }}
          />

          <TextField
            label="كلمة المرور الجديدة (اختياري)"
            placeholder="اتركها فارغة للإبقاء على القديمة"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon sx={{ color: themeColors.text.secondary }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                "&:hover fieldset": {
                  borderColor: themeColors.secondary.main,
                },
                "&.Mui-focused fieldset": {
                  borderColor: themeColors.secondary.main,
                },
              },
            }}
          />

          <TextField
            label="الاسم الكامل"
            placeholder="أدخل الاسم"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon sx={{ color: themeColors.text.secondary }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                "&:hover fieldset": {
                  borderColor: themeColors.secondary.main,
                },
                "&.Mui-focused fieldset": {
                  borderColor: themeColors.secondary.main,
                },
              },
            }}
          />

          <TextField
            label="رقم الهاتف"
            placeholder="+966 5xxxxxxxx"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PhoneIcon sx={{ color: themeColors.text.secondary }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                "&:hover fieldset": {
                  borderColor: themeColors.secondary.main,
                },
                "&.Mui-focused fieldset": {
                  borderColor: themeColors.secondary.main,
                },
              },
            }}
          />

          <TextField
            select
            label="حالة الحساب (Active)"
            value={active}
            onChange={(e) => setActive(e.target.value)}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <ToggleOnIcon sx={{ color: themeColors.text.secondary }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                "&:hover fieldset": {
                  borderColor: themeColors.secondary.main,
                },
                "&.Mui-focused fieldset": {
                  borderColor: themeColors.secondary.main,
                },
              },
            }}
          >
            <MenuItem value="">- اختر الحالة -</MenuItem>
            <MenuItem value="1">
              <Box display="flex" alignItems="center" gap={1}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: themeColors.success,
                  }}
                />
                يعمل (1)
              </Box>
            </MenuItem>
            <MenuItem value="0">
              <Box display="flex" alignItems="center" gap={1}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: themeColors.error,
                  }}
                />
                لا يعمل (0)
              </Box>
            </MenuItem>
          </TextField>
        </Box>

        <Box
          sx={{
            mt: 3,
            p: 2,
            bgcolor: `${themeColors.warning}10`,
            borderRadius: 2,
            border: `1px solid ${themeColors.warning}30`,
          }}
        >
          <Typography variant="caption" sx={{ color: themeColors.text.secondary }}>
            ⚠️ <strong>تنبيه:</strong> سيتم تحديث البيانات المُدخلة فقط. الحقول الفارغة لن تتأثر.
          </Typography>
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2.5, gap: 1 }}>
        <Button
          onClick={onClose}
          disabled={loading}
          variant="outlined"
          sx={{
            borderRadius: 2,
            px: 3,
            borderColor: themeColors.border.main,
            color: themeColors.text.secondary,
            "&:hover": {
              borderColor: themeColors.text.primary,
              bgcolor: "transparent",
            },
          }}
        >
          إلغاء
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading}
          sx={{
            borderRadius: 2,
            px: 4,
            bgcolor: themeColors.secondary.main,
            boxShadow: `0 4px 12px ${themeColors.secondary.main}40`,
            "&:hover": {
              bgcolor: themeColors.secondary.dark,
              boxShadow: `0 6px 16px ${themeColors.secondary.main}60`,
            },
            "&:disabled": {
              bgcolor: themeColors.text.light,
            },
          }}
        >
          {loading ? (
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={20} color="inherit" />
              <span>جاري الحفظ...</span>
            </Box>
          ) : (
            "حفظ التغييرات"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditBrokerAccountDialog;
